import cloneDeep from 'lodash-es/cloneDeep.js';
import mapValues from 'lodash-es/mapValues.js';
import { DimensionalAttribute, DimensionalLevelAttribute } from './dimensional-model/attributes.js';
import { isCascadingFilter } from './dimensional-model/filters/filters.js';
import { createFilterFromJaqlInternal } from './dimensional-model/filters/utils/filter-from-jaql-util.js';
import * as measureFactory from './dimensional-model/measures/factory.js';
import { DimensionalBaseMeasure } from './dimensional-model/measures/measures.js';
import { isDatetime, isNumber } from './dimensional-model/simple-column-types.js';
import { AggregationTypes, DataType, DateLevels, JaqlSortDirection, MetadataTypes, Sort, } from './dimensional-model/types.js';
/**
 * A more performant, but slightly bulkier, RFC4122v4 implementation. Performance is improved by minimizing calls to random()
 *
 * @internal
 */
export const guidFast = function (len) {
    if (!len) {
        len = 20;
    }
    const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz'.split('');
    const uuid = new Array(len);
    let rnd = 0;
    let r;
    for (let i = 0; i < len; i += 1) {
        if (i > 0 && i % 5 == 0) {
            uuid[i] = '-';
            continue;
        }
        if (rnd <= 0x02) {
            rnd = (0x2000000 + Math.random() * 0x1000000) | 0;
        }
        r = rnd & 0xf;
        rnd = rnd >> 4;
        uuid[i] = chars[i == 19 ? (r & 0x3) | 0x8 : r];
    }
    return uuid.join('');
};
/**
 * Function to separate list of filters from the provided relation and get logical relations for jaql
 *
 * @internal
 */
export const getFilterListAndRelationsJaql = (filterRelations) => {
    if (!filterRelations) {
        return { filters: undefined, relations: undefined };
    }
    if (Array.isArray(filterRelations)) {
        return { filters: filterRelations, relations: undefined };
    }
    const filters = new Set();
    function traverseCascade(cascade) {
        const [firstFilter, ...restFilters] = cascade;
        filters.add(firstFilter);
        if (!restFilters.length) {
            return traverse({ instanceid: firstFilter.config.guid });
        }
        const left = traverse(firstFilter);
        const right = traverseCascade(restFilters);
        return { left, right, operator: 'AND' };
    }
    function traverse(node) {
        if (!node)
            return node;
        if (isFilter(node)) {
            if (isCascadingFilter(node)) {
                return traverseCascade(node.filters);
            }
            filters.add(node);
            return { instanceid: node.config.guid };
        }
        else {
            if ('left' in node) {
                node.left = traverse(node.left);
            }
            if ('right' in node) {
                node.right = traverse(node.right);
            }
            if ('composeCode' in node) {
                delete node.composeCode;
            }
            return node;
        }
    }
    // Create a deep copy of filterRelations to avoid mutation
    const copiedFilterRelations = cloneDeep(filterRelations);
    const relations = traverse(copiedFilterRelations);
    return { filters: Array.from(filters), relations };
};
function isFilter(node) {
    return 'config' in node && 'guid' in node.config;
}
/**
 * Gets the name of the data source
 *
 * @internal
 */
export function getDataSourceName(dataSource) {
    return typeof dataSource === 'string' ? dataSource : dataSource.title;
}
/**
 * Checks if the provided 'dataSource' is a data source info structure that contains more than just the data source name.
 *
 * @internal
 */
export function isDataSourceInfo(dataSource) {
    return typeof dataSource === 'object' && 'type' in dataSource && 'title' in dataSource;
}
/**
 * Converts a JaqlDataSource to DataSource.
 *
 * @internal
 */
export function convertDataSource(jaqlDataSource) {
    return {
        id: jaqlDataSource.id,
        address: jaqlDataSource.address,
        title: jaqlDataSource.title,
        type: jaqlDataSource.live ? 'live' : 'elasticube',
    };
}
/**
 * Converts a DataSource to a description of data source used in JAQL.
 *
 * @internal
 */
export function convertJaqlDataSource(dataSource) {
    return isDataSourceInfo(dataSource)
        ? {
            title: dataSource.title,
            live: dataSource.type === 'live',
        }
        : {
            title: dataSource,
            live: false,
        };
}
/**
 * Converts a DataSource to JaqlDataSourceForDto.
 *
 * @internal
 */
export function convertJaqlDataSourceForDto(dataSource) {
    var _a;
    if (isDataSourceInfo(dataSource)) {
        return {
            title: dataSource.title,
            live: dataSource.type === 'live',
            id: (_a = dataSource.id) !== null && _a !== void 0 ? _a : '',
            address: dataSource.address,
        };
    }
    else {
        return {
            title: dataSource,
            id: '',
        };
    }
}
/**
 * Converts a string to a Sort enum
 *
 * @param sort - The string to convert
 * @returns The converted Sort enum
 * @internal
 */
export function convertSort(sort) {
    if (sort) {
        return sort === 'asc' ? Sort.Ascending : Sort.Descending;
    }
    else {
        return Sort.None;
    }
}
/**
 * Converts a SortDirection to a Sort enum
 *
 * @param sortDirection - The SortDirection to convert
 * @returns The converted Sort enum
 * @internal
 */
export function convertSortDirectionToSort(sortDirection) {
    switch (sortDirection) {
        case 'sortAsc':
            return Sort.Ascending;
        case 'sortDesc':
            return Sort.Descending;
        default:
            return Sort.None;
    }
}
/**
 * Creates a filter from a JAQL object.
 *
 * @param jaql - The filter JAQL object.
 * @param instanceid - Optional instance ID.
 * @param disabled - Optional disabled flag.
 * @param locked - Optional locked flag.
 * @returns - The created Filter object.
 * @internal
 */
export const createFilterFromJaql = (jaql, instanceid, disabled = false, locked = false) => {
    // translation logic is based on FilterJaqlInternal type (from internal modern-analytics-filters)
    // TODO reconcile FilterJaql and FilterJaqlInternal
    const hasBackgroundFilter = jaql.filter.filter && !('turnedOff' in jaql.filter.filter);
    const guid = instanceid || guidFast();
    const filter = createFilterFromJaqlInternal(jaql, guid);
    if (hasBackgroundFilter) {
        const backgroundFilter = createFilterFromJaqlInternal(Object.assign(Object.assign({}, jaql), { filter: jaql.filter.filter }), `${instanceid}-bg`);
        const config = filter.config;
        filter.config = Object.assign(Object.assign({}, config), { backgroundFilter });
        return filter;
    }
    filter.config = Object.assign(Object.assign({}, filter.config), { originalFilterJaql: jaql, disabled, locked });
    return filter;
};
/**
 * Extracts the table and column names from the given expression string.
 *
 * @internal
 */
export function parseExpression(expression) {
    const [table, column] = expression.slice(1, -1).split('.');
    return {
        table,
        column: column
            ? column
                // in case of Date we have to remove the (Calendar) part
                .replace('(Calendar)', '')
                .trim()
            : '',
    };
}
/**
 * Retrieves the table value from the attribute.
 *
 * @internal
 */
export function getTableNameFromAttribute(attribute) {
    return parseExpression(attribute.expression).table;
}
/**
 * Retrieves the column value from the attribute.
 *
 * @internal
 */
export function getColumnNameFromAttribute(attribute) {
    return parseExpression(attribute.expression).column;
}
/**
 * Gets the sort type based on the jaql sort direction.
 *
 * @param jaqlSort - The jaql sort direction.
 * @returns  The sort direction.
 * @internal
 */
export function getSortType(jaqlSort) {
    switch (jaqlSort) {
        case JaqlSortDirection.ASC:
            return 'sortAsc';
        case JaqlSortDirection.DESC:
            return 'sortDesc';
        default:
            return 'sortNone';
    }
}
/**
 * Creates an attribute or level attribute from the provided parameters
 *
 * @returns attribute or level attribute
 * @internal
 */
export const createAttributeHelper = ({ expression, dataType, granularity, format, sort, title, panel, dataSource, }) => {
    const column = parseExpression(expression).column;
    const sortEnum = convertSort(sort);
    const isDataTypeDatetime = dataType === DataType.DATETIME;
    if (isDataTypeDatetime) {
        const levelAttribute = new DimensionalLevelAttribute(title !== null && title !== void 0 ? title : column, expression, granularity || DateLevels.Years, format ||
            DimensionalLevelAttribute.getDefaultFormatForGranularity(granularity || DateLevels.Years), undefined, sortEnum, dataSource, undefined, panel);
        return levelAttribute;
    }
    const attributeType = !dataType || isNumber(dataType) ? MetadataTypes.NumericAttribute : MetadataTypes.TextAttribute;
    const attribute = new DimensionalAttribute(title !== null && title !== void 0 ? title : column, expression, attributeType, undefined, sortEnum, dataSource, undefined, panel);
    return attribute;
};
/**
 * Creates a measure from the provided parameters
 *
 * @returns measure
 * @internal
 */
export const createMeasureHelper = ({ expression, dataType, agg, granularity, format, sort, title, dataSource, }) => {
    const attribute = createAttributeHelper({
        expression,
        dataType,
        granularity,
        format,
        sort,
        title,
        dataSource,
    });
    const tranformedAgg = DimensionalBaseMeasure.aggregationFromJAQL(agg);
    const column = parseExpression(expression).column;
    const updatedTitle = title !== null && title !== void 0 ? title : `${tranformedAgg} ${column}`;
    switch (tranformedAgg) {
        case AggregationTypes.Sum:
            return measureFactory.sum(attribute, updatedTitle, format);
        case AggregationTypes.Average:
            return measureFactory.avg(attribute, updatedTitle, format);
        case AggregationTypes.Min:
            return measureFactory.min(attribute, updatedTitle, format);
        case AggregationTypes.Max:
            return measureFactory.max(attribute, updatedTitle, format);
        case AggregationTypes.Count:
            return measureFactory.count(attribute, updatedTitle, format);
        case AggregationTypes.CountDistinct:
            return measureFactory.countDistinct(attribute, updatedTitle, format);
        case AggregationTypes.Median:
            return measureFactory.median(attribute, updatedTitle, format);
        case AggregationTypes.Variance:
            return measureFactory.aggregate(attribute, AggregationTypes.Variance, updatedTitle, format);
        case AggregationTypes.StandardDeviation:
            return measureFactory.aggregate(attribute, AggregationTypes.StandardDeviation, updatedTitle, format);
        default:
            return measureFactory.sum(attribute, updatedTitle, format);
    }
};
/**
 * Creates a measure from the provided parameters
 *
 * @returns calculated measure
 * @internal
 */
export const createCalculatedMeasureHelper = (jaql) => {
    var _a;
    const context = mapValues((_a = jaql.context) !== null && _a !== void 0 ? _a : {}, (jaqlContextValue) => {
        if (typeof jaqlContextValue === 'string') {
            return jaqlContextValue;
        }
        return jaqlContextValue && createDimensionalElementFromJaql(jaqlContextValue);
    });
    return measureFactory.customFormula(jaql.title, jaql.formula, context);
};
/**
 * Creates a dimensional element from a JAQL object.
 *
 * @param jaql - The JAQL object.
 * @param datetimeFormat - The datetime format.
 * @returns The created dimensional element.
 * @internal
 */
export function createDimensionalElementFromJaql(jaql, datetimeFormat, panel) {
    const isFilterJaql = 'filter' in jaql;
    if (isFilterJaql) {
        return createFilterFromJaql(jaql);
    }
    const isFormulaJaql = 'formula' in jaql;
    if (isFormulaJaql) {
        return createCalculatedMeasureHelper(jaql);
    }
    const dataSource = 'datasource' in jaql ? jaql.datasource : undefined;
    const hasAggregation = !!jaql.agg;
    if (hasAggregation) {
        return createMeasureHelper({
            expression: jaql.dim,
            dataType: jaql.datatype,
            agg: jaql.agg || '',
            granularity: getGranularityFromJaql(jaql),
            format: datetimeFormat,
            sort: jaql.sort,
            title: jaql.title,
            dataSource,
        });
    }
    return createAttributeHelper({
        expression: jaql.dim,
        dataType: jaql.datatype,
        granularity: getGranularityFromJaql(jaql),
        format: datetimeFormat,
        sort: jaql.sort,
        title: jaql.title,
        panel: panel,
        dataSource,
    });
}
/**
 * Returns the granularity from the provided JAQL object.
 *
 * @param jaql - The JAQL object.
 * @returns string.
 * @internal
 */
export function getGranularityFromJaql(jaql) {
    return (jaql === null || jaql === void 0 ? void 0 : jaql.datatype) && isDatetime(jaql.datatype)
        ? DimensionalLevelAttribute.translateJaqlToGranularity(jaql)
        : undefined;
}
//# sourceMappingURL=utils.js.map